import React from 'react';

import { Col } from './Flex';
import { Text } from './Text';

import { cn } from 'domain/core';
import { createId } from 'domain/utils';

type FormControlCtx = {
	id: string;
};

const FormControlContext = React.createContext<Partial<FormControlCtx>>({});

const useFormControlContext = () => React.useContext(FormControlContext);

interface FormControlProps extends React.PropsWithChildren {
	className?: string;
	id?: string;
}
const FormControl = (props: FormControlProps) => {
	return (
		<FormControlContext.Provider
			value={{
				id: props.id ?? createId('form_control_'),
			}}>
			<Col
				className={cn(
					'form-control',
					'h-auto w-auto items-start gap-0',
					props.className
				)}>
				{props.children}
			</Col>
		</FormControlContext.Provider>
	);
};

interface LabelProps extends React.PropsWithChildren {
	className?: string;
	required?: boolean;
}
const Label = (props: LabelProps) => {
	const { id } = useFormControlContext();

	return (
		<label
			htmlFor={`${id}`}
			className={cn(
				'form-control-label',
				'w-full',
				'text-sm text-neutral-700',
				props.className
			)}>
			{props.children}
			{props.required && (
				<Text
					className={cn(
						'form-control-label-required-mark',
						'text-sm text-primary-600'
					)}>
					*
				</Text>
			)}
		</label>
	);
};

interface DescriptionProps extends React.PropsWithChildren {
	className?: string;
}
const Description = (props: DescriptionProps) => {
	const { id } = useFormControlContext();

	return (
		<Text
			className={cn(
				'form-control-description',
				'w-full',
				'text-xs text-neutral-400',
				props.className
			)}
			id={`${id}_description`}>
			{props.children}
		</Text>
	);
};

interface ErrorProps extends React.PropsWithChildren {
	show?: boolean;
	className?: string;
}
const Error = (props: ErrorProps) => {
	const { show, className, children } = props;
	const { id } = useFormControlContext();

	return (
		<Text
			className={cn(
				'form-control-error',
				'w-full transition-all',
				'text-xs text-red-500',
				show ? 'visible' : 'invisible',
				className
			)}
			id={`${id}_error`}>
			{children}
		</Text>
	);
};

type ControlProps = React.PropsWithChildren;
const Control = (props: ControlProps) => {
	const { id } = useFormControlContext();

	if (React.Children.count(props.children) === 1) {
		const child = React.Children.only(props.children);
		if (React.isValidElement(child)) {
			return React.cloneElement<any>(child, {
				'aria-labelledby': `${id}_label`,
				'aria-describedby': `${id}_description`,
				id,
			});
		}
	}
	return <>{props.children}</>;
};

FormControl.Label = Label;
FormControl.Description = Description;
FormControl.Error = Error;
FormControl.Control = Control;

FormControl.displayName = 'FormControl';

export { FormControl };
